import React, { useEffect, useRef, useState } from "react";
import { Dropdown } from "semantic-ui-react";
import { useAppDispatch } from "../../../../../stores/CommunityStore";
import { createComment } from "../../../../../stores/CommunityStore/comments/actions/createComment";
import { deleteComment } from "../../../../../stores/CommunityStore/comments/actions/deleteComment";
import {
  likePost,
  unlikePost,
} from "../../../../../stores/CommunityStore/posts/actions/likePost";
import { deletePost } from "../../../../../stores/CommunityStore/posts/actions/deletePost";
import { pinPost } from "../../../../../stores/CommunityStore/posts/actions/pinPost";
import { updatePost } from "../../../../../stores/CommunityStore/posts/actions/updatePost";
import AttachmentCard from "../Composer/AttachmentCard";
import Composer from "../Composer";
import Avatar from "./Avatar";
import Body from "./Body";
import { ChevronDownIcon, CommentBubbleIcon } from "./InlineIcons";
import { linkAttachmentFromMetadata } from "./linkAttachmentFromMetadata";
import LikeButton from "./LikeButton";
import { stripRedundantLinkParagraph } from "./stripRedundantLinkParagraph";
import Thread from "./Thread";
import { PostThreadProps } from "./types";

const Article = ({
  post,
  currentUserName,
  currentUserProfilePictureUrl,
  onLoadMore,
  onReplySubmit,
  onDeleteReply,
}: PostThreadProps) => {
  const dispatch = useAppDispatch();
  const [expanded, setExpanded] = useState(Boolean(post.initiallyExpanded));
  const [isEditing, setIsEditing] = useState(false);
  const postRef = useRef<HTMLElement>(null);
  const hasThread = Boolean(post.threadId && post.threadLabel && post.replies);
  const replyCount = post.replyCount ?? 0;

  useEffect(() => {
    if (!expanded || !post.targetReplyId) return;

    const scrollTimer = window.setTimeout(() => {
      document
        .getElementById(`community-reply-${post.targetReplyId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);

    return () => window.clearTimeout(scrollTimer);
  }, [expanded, post.targetReplyId]);

  const collapseThread = () => {
    setExpanded(false);
    postRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  const toggleThread = () => {
    if (hasThread) setExpanded(!expanded);
  };

  const togglePostLike = (liked: boolean) => {
    if (!post.postId) return;

    dispatch(
      liked
        ? unlikePost({ postId: post.postId })
        : likePost({ postId: post.postId }),
    );
  };

  const submitReply = async (commentId: number | null, value: string) => {
    if (!post.postId) return;

    await dispatch(
      createComment({
        postId: post.postId,
        commentId: commentId || undefined,
        content: value,
      }),
    );
  };

  const deleteReply = (commentId: number, replyId?: number) => {
    if (!post.postId) return;

    dispatch(deleteComment({ postId: post.postId, commentId, replyId }));
  };

  const removePost = () => {
    if (!post.postId) return;

    dispatch(deletePost({ postId: post.postId }));
  };

  const togglePin = () => {
    if (!post.postId) return;

    dispatch(pinPost({ postId: post.postId, pinned: !post.isPinned }));
  };

  const hasLinkRows = Boolean(post.attachments?.links?.length);
  // Legacy posts only: they smuggled their link's URL into content as its
  // own trailing paragraph (see submitPost's history) so the backend had
  // something to scrape, since there was no structured link field yet.
  // Posts with real community_post_links rows never did that, so there's
  // nothing to strip for them.
  const metadataLink =
    !hasLinkRows && post.metadata ? linkAttachmentFromMetadata(post.metadata) : null;
  const displayBody = metadataLink
    ? stripRedundantLinkParagraph(post.body)
    : post.body;

  const initialLinks = post.attachments?.links?.length
    ? post.attachments.links
    : metadataLink
      ? [metadataLink]
      : [];

  const startEditing = () => setIsEditing(true);

  const cancelEditing = () => setIsEditing(false);

  const saveEdit = async (
    markdown: string,
    linkUrls: string[],
    removeImages: boolean,
    image?: File,
  ) => {
    if (!post.postId) return;

    await dispatch(
      updatePost(post.postId, markdown, removeImages, linkUrls, image),
    );
    setIsEditing(false);
  };

  return (
    <article className="post" data-post-id={post.id} ref={postRef}>
      <div className="post-header">
        <Avatar
          className="post-avatar"
          name={post.author}
          profilePictureUrl={post.profilePictureUrl}
        />
        <div className="post-meta">
          <div className="post-author">
            {post.author}
            {post.isAuthor && <span className="op-pill">Author</span>}
            {post.isPinned && <span className="pinned-pill">Pinned</span>}
          </div>
          <div className="post-time">{post.time}</div>
        </div>
        {(post.canDelete || post.canEdit || post.canPin) && (
          <Dropdown icon="ellipsis horizontal" pointing="top right">
            <Dropdown.Menu>
              {post.canEdit && (
                <Dropdown.Item text="Edit" onClick={startEditing} />
              )}
              {post.canPin && (
                <Dropdown.Item
                  text={post.isPinned ? "Unpin" : "Pin"}
                  onClick={togglePin}
                />
              )}
              {post.canDelete && (
                <Dropdown.Item text="Delete" onClick={removePost} />
              )}
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>
      {isEditing ? (
        <Composer
          mode="edit"
          initialContent={displayBody}
          initialLinks={initialLinks}
          existingImages={post.attachments?.images ?? []}
          onSave={saveEdit}
          onCancel={cancelEditing}
        />
      ) : (
        <Body content={displayBody} />
      )}
      {!isEditing &&
        ((post.attachments &&
          (post.attachments.images.length > 0 ||
            post.attachments.links.length > 0)) ||
          metadataLink) && (
          <div className="community-composer__previews post-attachments">
            {post.attachments?.images.map((image) => (
              <AttachmentCard key={image.id} image={image} />
            ))}
            {post.attachments?.links.map((link) => (
              <AttachmentCard key={link.id} link={link} />
            ))}
            {metadataLink && (
              <AttachmentCard key="metadata-link" link={metadataLink} />
            )}
          </div>
        )}
      <div className="post-actions">
        <LikeButton
          className="post-action "
          initialCount={post.likes}
          initiallyLiked={post.liked}
          label="Like this post"
          likedLabel="You liked this post"
          onToggle={togglePostLike}
        />
        <button
          className="post-action replies-btn"
          type="button"
          aria-expanded={expanded}
          aria-controls={post.threadId}
          onClick={toggleThread}
        >
          <CommentBubbleIcon /> {replyCount} replies{" "}
          <span className="chev" aria-hidden="true">
            <ChevronDownIcon />
          </span>
        </button>
      </div>

      {expanded && post.threadId && post.threadLabel && post.replies && (
        <Thread
          id={post.threadId}
          label={post.threadLabel}
          replyCount={replyCount}
          replies={post.replies}
          remainingReplies={post.remainingReplies}
          currentUserName={currentUserName}
          currentUserProfilePictureUrl={currentUserProfilePictureUrl}
          initiallyReplyingToId={post.initiallyReplyingToId}
          onCollapse={collapseThread}
          onLoadMore={onLoadMore}
          onReplySubmit={onReplySubmit || submitReply}
          onDeleteReply={onDeleteReply || deleteReply}
        />
      )}
    </article>
  );
};

export default Article;
